
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { smoothScrollTo } from '../../utils/scroll';
import { useCookieConsent } from '../../hooks/useCookieConsent';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openSettingsModal } = useCookieConsent();

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById('contact');
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        smoothScrollTo(offsetPosition, 800);
      }
    } else {
      navigate('/', { state: { targetId: 'contact' } });
    }
  };

  const handleScrollTo = (e, id) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        smoothScrollTo(offsetPosition, 800);
      }
    } else {
      navigate('/', { state: { targetId: id } });
    }
  };

  return (
    <footer className="bg-[#f8faff] pt-16 pb-8 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand and Description */}
          <div className="col-span-1">
            <div className="mb-6">
              <img src="/assets/logo.png" alt="Billwave" className="h-8" />
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4 text-left">
            － 請求業務に新しい波を －
            </p>
          </div>

          {/* Links Columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:col-span-2 gap-8">
            {/* サービス */}
            <div className="text-left">
              <h3 className="font-bold text-blue-600 mb-6">サービス</h3>
              <ul className="space-y-4 text-sm text-gray-600">
                <li><a href="#features" onClick={(e) => handleScrollTo(e, 'features')} className="hover:text-blue-600 transition-colors">機能一覧</a></li>
                <li><a href="#flow" onClick={(e) => handleScrollTo(e, 'flow')} className="hover:text-blue-600 transition-colors">ご利用の流れ</a></li>
                <li><a href="#pricing" onClick={(e) => handleScrollTo(e, 'pricing')} className="hover:text-blue-600 transition-colors">料金プラン</a></li>
                <li><a href="#faq" onClick={(e) => handleScrollTo(e, 'faq')} className="hover:text-blue-600 transition-colors">よくある質問</a></li>
                <li><Link to="/announcements" className="hover:text-blue-600 transition-colors">お知らせ</Link></li>
              </ul>
            </div>

            {/* サポート */}
            <div className="text-left">
              <h3 className="font-bold text-blue-600 mb-6">サポート</h3>
              <ul className="space-y-4 text-sm text-gray-600">
                <li><Link to="/support/how-to-use" className="hover:text-blue-600 transition-colors">使い方</Link></li>
                <li><Link to="/support/csv-format" className="hover:text-blue-600 transition-colors">CSVフォーマット</Link></li>
                <li><Link to="/faq" className="hover:text-blue-600 transition-colors">FAQ</Link></li>
                <li><a href="#contact" onClick={handleContactClick} className="hover:text-blue-600 transition-colors">お問い合わせ</a></li>
              </ul>
            </div>

            {/* 運営会社 */}
            <div className="col-span-2 md:col-span-1 text-left">
              <h3 className="font-bold text-blue-600 mb-6">運営会社</h3>
              <ul className="space-y-4 text-sm text-gray-600">
                <li><a href="https://s-nal.jp/company/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">会社概要</a></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">利用規約</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-blue-600 transition-colors">個人情報保護方針</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-blue-600 transition-colors">Cookieポリシー</Link></li>
                <li>
                  <button
                    onClick={openSettingsModal}
                    className="text-gray-600 hover:text-blue-600 transition-colors text-left"
                  >
                    Cookie設定
                  </button>
                </li>
                <li><Link to="/tokushoho" className="hover:text-blue-600 transition-colors">特定商取引法に基づく表記</Link></li>
              </ul>
            </div>
          </div>

          {/* Contact Button Section */}
          <div className="col-span-1 text-left">
            <h3 className="font-bold text-blue-600 mb-6">お問い合わせ</h3>
            <p className="text-xs text-gray-500 mb-4">
              ご不明点やご相談は<br />
              お気軽にお問い合わせください。
            </p>
            <a
              href="#contact"
              onClick={handleContactClick}
              className="inline-flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 font-bold py-3 px-4 rounded-lg hover:bg-blue-50 transition-colors group"
            >
              <Mail className="w-5 h-5" />
              <span>お問い合わせフォームへ</span>
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-gray-100 text-right">
          <p className="text-gray-500 text-sm">
            &copy; 2026 SIGNAL CORPORATION
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
